import { createProvider } from '@frontend/store';
import { genUUIDV4 } from '@frontend/string';
import { executeUpdateActions } from './helpers';
import { abortUpload } from './media-upload/abort-controllers';
import { MediaAttachment } from './media-upload/types';
import { ESignValue, isESignSigned, isSameSignature } from './signature/esign-value';
import { DynamicFormField, DynamicFormSection, SchemaFormFieldCondition } from './types';

export type DynamicFormStoreState = {
  schemaId: string;
  formSections: DynamicFormSection[];
  formFieldsMap: Record<string, DynamicFormField>;
  conditionsMap: Record<string, SchemaFormFieldCondition>;
  activeSectionIndex: number;
  // Transient per-attachment upload state (loader/retry/error), keyed by attachment id.
  attachments: Record<string, MediaAttachment>;
  /** Session-wide eSign history shared by every eSign field (newest first). Never drafted. */
  eSignHistory: ESignValue[];
  /** Session-wide drawn PDF signatures, reusable across PDF fields (newest first). Never drafted. */
  pdfSignatures: SavedPdfSignature[];
};

export interface SavedPdfSignature {
  id: string;
  /** Trimmed transparent PNG. In memory only. */
  dataUrl: string;
  /** Natural size in CSS px at the time of drawing. */
  width: number;
  height: number;
  signedBy: string;
}

const MAX_SESSION_SIGNATURES = 10;

export type DynamicFormStoreActions = {
  setActiveSectionIndex: (index: number) => void;
  setFieldValue: (key: string, value: unknown) => void;
  setFieldHidden: (key: string, hidden: boolean) => void;
  setFieldValues: (values: Record<string, unknown>) => void;
  executeFieldUpdates: (fieldKey: string) => Record<string, unknown>;
  syncSchema: (newState: Pick<DynamicFormStoreState, 'formSections' | 'formFieldsMap' | 'conditionsMap'>) => void;
  addAttachment: (attachment: MediaAttachment) => void;
  updateAttachment: (id: string, patch: Partial<MediaAttachment>) => void;
  removeAttachment: (id: string) => void;
  clearFieldAttachments: (fieldKey: string) => void;
  addESignHistory: (value: ESignValue) => void;
  addPdfSignature: (signature: SavedPdfSignature) => void;
  clearAttachments: () => void;
};

export type DynamicFormStore = DynamicFormStoreState & DynamicFormStoreActions;

const initialState: DynamicFormStoreState = {
  schemaId: 'initial',
  formSections: [],
  formFieldsMap: {},
  conditionsMap: {},
  activeSectionIndex: 0,
  attachments: {},
  eSignHistory: [],
  pdfSignatures: [],
};

const revokeAttachmentPreviewUrls = (attachments: MediaAttachment[]) => {
  attachments.forEach(({ previewUrl }) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  });
};

const removeAttachments = (attachments: MediaAttachment[]) => {
  attachments.forEach(({ id }) => abortUpload(id));
  revokeAttachmentPreviewUrls(attachments);
};

export const {
  Provider: DynamicFormStoreProvider,
  useStore: useDynamicFormStore,
  useNonReactiveStore: useNonReactiveDynamicFormStore,
} = createProvider<DynamicFormStore>()((set) => ({
  ...initialState,

  setActiveSectionIndex: (index) =>
    set(({ formSections }) => ({
      activeSectionIndex: Math.max(0, Math.min(index, formSections.length - 1)),
    })),
  setFieldValue: (key, value) =>
    set(({ formFieldsMap }) => {
      const field = formFieldsMap[key];
      if (!field) return {};
      return {
        formFieldsMap: { ...formFieldsMap, [key]: { ...field, value } },
      };
    }),
  setFieldHidden: (key, hidden) =>
    set(({ formFieldsMap }) => {
      const field = formFieldsMap[key];
      if (!field) return {};
      return {
        formFieldsMap: { ...formFieldsMap, [key]: { ...field, hidden } },
      };
    }),
  setFieldValues: (values) =>
    set(({ formFieldsMap }) => {
      const updatedFieldsMap = Object.entries(formFieldsMap).reduce<Record<string, DynamicFormField>>(
        (acc, [key, field]) => {
          if (values[key] !== undefined) {
            acc[key] = { ...field, value: values[key] };
          } else {
            acc[key] = field;
          }
          return acc;
        },
        {}
      );
      return { formFieldsMap: updatedFieldsMap };
    }),
  syncSchema: (newState) =>
    set(({ formFieldsMap }) => ({
      formSections: newState.formSections,
      conditionsMap: newState.conditionsMap,
      formFieldsMap: Object.fromEntries(
        Object.entries(newState.formFieldsMap).map(([key, newField]) => [
          key,
          {
            ...newField,
            value: newField.resetOnSchemaChange ? undefined : formFieldsMap[key]?.value ?? newField.value,
          },
        ])
      ),
      schemaId: genUUIDV4(),
    })),

  executeFieldUpdates: (fieldKey) => {
    let updates: Record<string, unknown> = {};

    set((state) => {
      const field = state.formFieldsMap[fieldKey];
      if (!field) return {};

      // Build current values from all fields in the store
      const currentValues = Object.entries(state.formFieldsMap).reduce<Record<string, unknown>>((acc, [key, f]) => {
        acc[key] = f.value;
        return acc;
      }, {});

      // Get update actions from conditions triggered by this field
      updates = executeUpdateActions({
        sourceConditionIds: field.sourceConditionIds,
        conditionsMap: state.conditionsMap,
        values: currentValues,
      });

      // Apply updates to formFieldsMap
      const updatedFieldsMap = { ...state.formFieldsMap };
      Object.entries(updates).forEach(([key, value]) => {
        if (updatedFieldsMap[key]) {
          updatedFieldsMap[key] = { ...updatedFieldsMap[key], value };
        }
      });

      return { formFieldsMap: updatedFieldsMap };
    });

    return updates;
  },
  addAttachment: (attachment) =>
    set(({ attachments }) => ({
      attachments: { ...attachments, [attachment.id]: attachment },
    })),
  updateAttachment: (id, patch) =>
    set(({ attachments }) => {
      const attachment = attachments[id];
      if (!attachment) return {};
      return { attachments: { ...attachments, [id]: { ...attachment, ...patch } } };
    }),
  removeAttachment: (id) =>
    set(({ attachments }) => {
      const attachment = attachments[id];
      if (!attachment) return {};
      removeAttachments([attachment]);
      const next = { ...attachments };
      delete next[id];
      return { attachments: next };
    }),
  clearFieldAttachments: (fieldKey) =>
    set(({ attachments }) => {
      const removedAttachments = Object.values(attachments).filter((attachment) => attachment.fieldKey === fieldKey);
      if (removedAttachments.length === 0) return {};
      removeAttachments(removedAttachments);
      const next = Object.fromEntries(
        Object.entries(attachments).filter(([, attachment]) => attachment.fieldKey !== fieldKey)
      );
      return { attachments: next };
    }),
  addESignHistory: (value) =>
    set(({ eSignHistory }) => {
      if (!isESignSigned(value)) return {};
      // De-duplicate by rendered signature; a newer entry replaces (and refreshes) an older one.
      const rest = eSignHistory.filter((entry) => !isSameSignature(entry, value));
      return { eSignHistory: [value, ...rest].slice(0, MAX_SESSION_SIGNATURES) };
    }),
  addPdfSignature: (signature) =>
    set(({ pdfSignatures }) => ({
      pdfSignatures: [signature, ...pdfSignatures.filter((entry) => entry.id !== signature.id)].slice(
        0,
        MAX_SESSION_SIGNATURES
      ),
    })),
  clearAttachments: () =>
    set(({ attachments }) => {
      removeAttachments(Object.values(attachments));
      return { attachments: {} };
    }),
}));
